import { useEffect, type ComponentType, type ReactNode } from "react";
import {
  BarChart3,
  BookOpen,
  Boxes,
  ChevronLeft,
  CircleHelp,
  ClipboardList,
  Contact,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageSquareText,
  Moon,
  PackageSearch,
  PanelRightClose,
  PanelRightOpen,
  Percent,
  Settings,
  Star,
  Sun,
  Tags,
  Truck,
  UserRound,
  UsersRound,
  ShieldCheck,
} from "lucide-react";
import {
  Link,
  NavLink,
  Outlet,
  ScrollRestoration,
  isRouteErrorResponse,
  useLocation,
  useNavigate,
  useRouteError,
} from "react-router-dom";
import { authApi } from "../lib/api";
import { cx } from "../lib/cx";
import { PERMISSIONS, hasPanelAccess, hasPermission, primaryRoleLabel, type PermissionKey } from "../lib/permissions";
import { useAuthStore } from "../store/auth-store";
import { useUiStore } from "../store/ui-store";
import { IconButton } from "./ui";

type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number; className?: string }>;
  end?: boolean;
  permission?: PermissionKey;
};

const navGroups: Array<{ label: string; items: NavItem[] }> = [
  {
    label: "نمای کلی",
    items: [{ to: "/", label: "داشبورد", icon: LayoutDashboard, end: true, permission: PERMISSIONS.dashboardView }],
  },
  {
    label: "فروش و کاتالوگ",
    items: [
      { to: "/products", label: "محصولات", icon: PackageSearch, permission: PERMISSIONS.productsView },
      { to: "/brands", label: "برندها", icon: Tags, permission: PERMISSIONS.taxonomyManage },
      { to: "/categories", label: "دسته‌بندی‌ها", icon: Boxes, permission: PERMISSIONS.taxonomyManage },
      { to: "/orders", label: "سفارش‌ها", icon: ClipboardList, permission: PERMISSIONS.ordersView },
      { to: "/coupons", label: "کدهای تخفیف", icon: Percent, permission: PERMISSIONS.marketingManage },
      { to: "/shipping", label: "روش‌های ارسال", icon: Truck, permission: PERMISSIONS.shippingManage },
    ],
  },
  {
    label: "محتوا و ارتباط",
    items: [
      { to: "/reviews", label: "دیدگاه‌ها", icon: Star, permission: PERMISSIONS.reviewsManage },
      { to: "/blog", label: "مجله", icon: BookOpen, permission: PERMISSIONS.contentManage },
      { to: "/faqs", label: "پرسش‌های متداول", icon: CircleHelp, permission: PERMISSIONS.contentManage },
      { to: "/contacts", label: "پیام‌های تماس", icon: MessageSquareText, permission: PERMISSIONS.supportManage },
    ],
  },
  {
    label: "حساب و سیستم",
    items: [
      { to: "/users", label: "کاربران و مشتریان", icon: UsersRound, permission: PERMISSIONS.customersView },
      { to: "/access", label: "تیم و سطح دسترسی", icon: ShieldCheck, permission: PERMISSIONS.staffManage },
      { to: "/settings", label: "تنظیمات", icon: Settings, permission: PERMISSIONS.settingsManage },
      { to: "/profile", label: "پروفایل و امنیت", icon: UserRound },
    ],
  },
];

export function ProtectedRoute() {
  const { accessToken, user, hydrated } = useAuthStore();
  const location = useLocation();

  if (!hydrated) {
    return (
      <div className="app-splash">
        <div className="flex flex-col items-center gap-5">
          <img src="/lia-logo-app.png" alt="لیا" />
          <span>در حال آماده‌سازی پنل مدیریت…</span>
        </div>
      </div>
    );
  }

  if (!accessToken) return <NavigateToLogin from={location.pathname} />;
  if (!hasPanelAccess(user)) return <ForbiddenPage />;
  return <AdminLayout />;
}

function NavigateToLogin({ from }: { from: string }) {
  const navigate = useNavigate();

  useEffect(() => {
    navigate("/login", { replace: true, state: { from } });
  }, [from, navigate]);

  return null;
}

function SidebarNavGroup({ label, items, compact, user }: { label: string; items: NavItem[]; compact: boolean; user: ReturnType<typeof useAuthStore.getState>["user"] }) {
  const visibleItems = items.filter((item) => !item.permission || hasPermission(user, item.permission));
  if (!visibleItems.length) return null;
  return (
    <div className="mb-5 last:mb-0">
      <p className={cx("sidebar__group-label", compact && "lg:hidden")}>{label}</p>
      <div className="space-y-1">
        {visibleItems.map(({ to, label: itemLabel, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => isActive ? "active" : ""}>
            <Icon size={18} />
            <span>{itemLabel}</span>
            <ChevronLeft className="nav-arrow" size={13} />
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export function AdminLayout() {
  const { user, refreshToken, clearSession } = useAuthStore();
  const ui = useUiStore();
  const navigate = useNavigate();
  const location = useLocation();

  const setSidebarOpen = ui.setSidebarOpen;

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, setSidebarOpen]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", ui.theme === "dark");
    document.documentElement.style.colorScheme = ui.theme;
  }, [ui.theme]);

  const logout = async () => {
    if (refreshToken) {
      try {
        await authApi.revoke(refreshToken);
      } catch {
        // Local logout must remain available even when revocation endpoint is unavailable.
      }
    }
    clearSession();
    navigate("/login", { replace: true });
  };

  return (
    <div className={cx("admin-shell", ui.theme === "dark" && "dark", ui.sidebarCompact && "is-compact")}>
      <aside className={cx("sidebar", ui.sidebarOpen && "is-open")}>
        <div className="sidebar__brand">
          <Link to="/">
            <span className="sidebar__logo-mark">
              <img src="/lia-logo-app.png" alt="لوگوی عطر لیا" />
            </span>
            <div>
              <strong>عطر لیا</strong>
              <span>پنل مدیریت فروشگاه</span>
            </div>
          </Link>
        </div>

        <nav className="sidebar__nav" aria-label="منوی مدیریت">
          {navGroups.map((group) => (
            <SidebarNavGroup key={group.label} label={group.label} items={group.items} compact={ui.sidebarCompact} user={user} />
          ))}
        </nav>

        <div className="sidebar__footer">
          <button type="button" onClick={() => void logout()}>
            <LogOut size={19} />
            <span>خروج امن از حساب</span>
          </button>
        </div>
      </aside>

      {ui.sidebarOpen ? <button className="sidebar-overlay" aria-label="بستن منو" onClick={() => ui.setSidebarOpen(false)} /> : null}

      <main className="main-area">
        <header className="topbar">
          <div className="topbar__start">
            <IconButton label="باز کردن منو" className="mobile-menu" onClick={ui.toggleSidebar}><Menu /></IconButton>
            <IconButton
              label={ui.sidebarCompact ? "بازکردن منو" : "جمع‌کردن منو"}
              className="desktop-compact"
              onClick={ui.toggleCompact}
            >
              {ui.sidebarCompact ? <PanelRightOpen /> : <PanelRightClose />}
            </IconButton>
            <div className="topbar__title">
              <span>پنل رسمی فروشگاه</span>
              <strong>مدیریت یکپارچه Lia Perfume</strong>
            </div>
          </div>

          <div className="topbar__actions">
            <IconButton label={ui.theme === "light" ? "فعال‌کردن تم شب" : "فعال‌کردن تم روشن"} onClick={ui.toggleTheme}>
              {ui.theme === "light" ? <Moon /> : <Sun />}
            </IconButton>
            <Link to="/profile" className="user-chip" title="پروفایل و امنیت حساب">
              <span>{user?.firstName?.[0]}{user?.lastName?.[0]}</span>
              <div>
                <strong>{user?.firstName} {user?.lastName}</strong>
                <small>{primaryRoleLabel(user)}</small>
              </div>
            </Link>
          </div>
        </header>

        <div className="page-content"><Outlet /></div>
        <ScrollRestoration />
      </main>
    </div>
  );
}

export function RequirePermission({ permission, children }: { permission: PermissionKey; children: ReactNode }) {
  const user = useAuthStore((state) => state.user);
  if (!hasPermission(user, permission)) return <ForbiddenPermissionPage />;
  return <>{children}</>;
}

function ForbiddenPermissionPage() {
  return <main className="standalone-state standalone-state--inside"><div><ShieldCheck className="mx-auto text-amber-500" size={52} /><h1>دسترسی کافی ندارید</h1><p>نقش فعلی شما مجوز ورود به این بخش را ندارد. برای تغییر دسترسی با مالک سیستم هماهنگ کنید.</p><Link className="button button--primary mt-5" to="/">بازگشت به داشبورد</Link></div></main>;
}

export function RouteErrorPage() {
  const error = useRouteError();
  const status = isRouteErrorResponse(error) ? error.status : null;
  const detail = error instanceof Error ? error.message : null;

  return (
    <main className="standalone-state route-error-page">
      <div>
        <CircleHelp className="mx-auto text-lia-700" size={58} />
        <p className="eyebrow mt-4">خطای پنل مدیریت</p>
        <h1>{status === 404 ? "صفحه پیدا نشد" : "این بخش به‌درستی بارگذاری نشد"}</h1>
        <p>
          {status === 404
            ? "آدرس درخواستی در پنل مدیریت وجود ندارد."
            : "یک خطای غیرمنتظره رخ داده است. صفحه را دوباره بارگذاری کنید و اگر مشکل ادامه داشت، گزارش خطا را بررسی کنید."}
        </p>
        {import.meta.env.DEV && detail ? (
          <code className="mt-4 block max-h-36 overflow-auto rounded-xl bg-slate-950 p-3 text-left text-[11px] leading-5 text-rose-200" dir="ltr">{detail}</code>
        ) : null}
        <div className="route-error-actions">
          <button className="button button--primary" type="button" onClick={() => window.location.reload()}>بارگذاری دوباره</button>
          <Link className="button button--ghost" to="/">بازگشت به داشبورد</Link>
        </div>
      </div>
    </main>
  );
}

export function ForbiddenPage() {
  const clearSession = useAuthStore((state) => state.clearSession);

  return (
    <main className="standalone-state">
      <div>
        <Contact className="mx-auto text-amber-500" size={56} />
        <h1>دسترسی مدیر لازم است</h1>
        <p>حساب واردشده مجوز مدیریت فروشگاه را ندارد. با حساب مدیر مجاز وارد شوید.</p>
        <button className="button button--primary mt-5" type="button" onClick={clearSession}>بازگشت به ورود</button>
      </div>
    </main>
  );
}

export function NotFoundPage() {
  return (
    <main className="standalone-state">
      <div>
        <BarChart3 className="mx-auto text-lia-700" size={56} />
        <h1>صفحه پیدا نشد</h1>
        <p>آدرس واردشده در پنل مدیریت وجود ندارد.</p>
        <Link className="button button--primary mt-5" to="/">بازگشت به داشبورد</Link>
      </div>
    </main>
  );
}
